/**
 * @file intlFormat.ts
 * @description The one place the app turns an active UI locale into formatted
 * dates and numbers.
 *
 * ## Why this file exists (SE-7966)
 *
 * The LPW epic localized *strings* — SE-6505 collapsed the offered locale list,
 * SE-6507 stood up the i18next runtime, SE-6512 extracted UI literals — but
 * nothing localized dates and numbers. The result was the same seam SE-6505
 * closed for the offered list, reopened one layer down: a user could set their
 * language to German, read German UI chrome, and see `4/23/2026` beside it.
 *
 * Four different locale sources were live simultaneously and none of them was
 * the user's `languagePreference`: a hardcoded `"en-US"`, a hardcoded
 * `"en-GB"`, an `undefined` that resolves to the DEVICE locale, and the device
 * locale read explicitly through `expo-localization`. The last two are the
 * sharpest — two users with the same account setting saw different output
 * depending on the phone they were holding.
 *
 * This module centralises the formatting the way `@/constants/locales`
 * centralised the locale list. Call sites pass the active UI locale in; they do
 * not read `expo-localization`, and they do not write a locale literal.
 *
 * ## The sanctioned exception (SE-8058)
 *
 * `@/utils/formatCurrencyShort` keeps two `"en-US"` literals on purpose.
 * Currency is fixed by product decision — the symbol does not follow the
 * account locale, and neither does its placement — which is why a German user
 * sees `1.234` minutes beside a `€1K` wage in one Squad Planning row. That is
 * accepted, not outstanding.
 *
 * It is also load-bearing: that file derives its symbol by formatting zero and
 * stripping the digits, a trick that only survives the string shape `en-US`
 * produces. Read its file header before localising it — the locale you would
 * reach for to check your work, `de-DE`, is the one that hides the breakage.
 *
 * **It is the only SANCTIONED exception, not the only remaining one.** Five
 * further `"en-US"` literals survive under `src/`, in
 * `formatNumberWithCommas.ts`, `playerFormatting.ts`,
 * `formatRelativeTimestamp.ts`, `generatePlayerReportPdf.ts` and
 * `player-detail/utils/index.ts`. Nothing has ruled on those; they are simply
 * undecided, and this section must not be read as covering them.
 *
 * ## The one conversion trap
 *
 * Catalogs and `SUPPORTED_UI_LOCALES` are keyed by `SupportedLocale` enum
 * values with an UNDERSCORE (`en_US`), while `Intl` requires BCP-47 with a
 * HYPHEN (`en-US`). {@link toBcp47} is the single place that conversion
 * happens.
 *
 * This matters more than a cosmetic mismatch suggests: passing `en_US` to
 * `Intl.DateTimeFormat` does **not** throw. It is a structurally valid language
 * subtag as far as the runtime's tag parser is concerned in some engines, and
 * where it is rejected the constructor falls back to the runtime default
 * locale. Either way the failure is SILENT and looks exactly like the bug this
 * module exists to remove. Never hand a `SupportedLocale` to an `Intl`
 * constructor directly — go through {@link formatDate} / {@link formatNumber},
 * or through {@link toBcp47} if you genuinely need the tag itself.
 * @module utils/intlFormat
 */
import { DEFAULT_UI_LOCALE, resolveDisplayLocale } from "@/constants/locales";

/**
 * Converts a `SupportedLocale` enum value to a BCP-47 language tag.
 *
 * `en_US` → `en-US`. The ONLY underscore-to-hyphen conversion in the codebase;
 * see the module docblock for why a second one is a bug rather than a
 * duplication.
 *
 * Total by construction: a tag that is already hyphenated passes through
 * unchanged, so this is safe to apply to a value of unknown provenance.
 * @param locale - A `SupportedLocale` value (`en_US`), or an already-hyphenated tag
 * @returns The BCP-47 tag (`en-US`)
 */
export function toBcp47(locale: string): string {
  return locale.replace(/_/gu, "-");
}

/**
 * Resolves any locale-ish input to the BCP-47 tag of an OFFERED locale.
 *
 * Routes through {@link resolveDisplayLocale} first, so a stranded preference
 * (`ja_JP`, dropped by LPW-1.0) degrades to the default rather than reaching
 * `Intl` as a locale whose formatting the app never offers. That keeps the
 * formatting answer identical to the answer every other locale surface gives
 * for the same stored value.
 *
 * **The hyphen has to be normalised OUT before resolving, not after.**
 * `findSupportedUiLocale` splits on `_` and compares against the underscored
 * `SUPPORTED_UI_LOCALES`, so a BCP-47 `de-DE` matches nothing and collapses to
 * `en_US` — a German caller silently getting English dates. That is this
 * ticket's own bug, re-introduced one layer up, and it is invisible without a
 * test that formats the same instant through both spellings. Normalising first
 * makes the function total over both shapes, which is what its callers assume.
 * @param locale - An active UI locale, stored `languagePreference`, or nothing
 * @returns A BCP-47 tag for one of `SUPPORTED_UI_LOCALES`
 */
export function resolveFormattingLocale(
  locale: string | null | undefined
): string {
  const underscored = locale?.replace(/-/gu, "_");
  return toBcp47(resolveDisplayLocale(underscored));
}

/**
 * Cache of constructed `Intl` formatters.
 *
 * `Intl.DateTimeFormat` and `Intl.NumberFormat` construction is expensive
 * enough to matter in a list row that formats a date per render. The call sites
 * this module replaced avoided that cost by hoisting the formatter to module
 * scope — which is precisely what made them locale-blind, since a module-scope
 * formatter is built once with whatever locale was active at import time and
 * can never follow a language change. Caching here keeps the performance
 * property without the correctness one.
 */
const formatterCache = new Map<
  string,
  Intl.DateTimeFormat | Intl.NumberFormat
>();

/**
 * Builds a stable cache key for a locale plus an options bag.
 *
 * Option keys are sorted so two call sites that pass the same options in a
 * different order share one formatter instead of building two.
 * @param kind - Which formatter family the key is for
 * @param locale - The BCP-47 tag
 * @param options - The formatter options, if any
 * @returns A cache key
 */
function cacheKey(
  kind: string,
  locale: string,
  options: Intl.DateTimeFormatOptions | Intl.NumberFormatOptions | undefined
): string {
  if (options === undefined) return `${kind}|${locale}|`;
  const bag = options as Record<string, unknown>;
  const entries = Object.keys(bag)
    .sort((a, b) => a.localeCompare(b))
    .map(key => `${key}:${String(bag[key])}`)
    .join(",");
  return `${kind}|${locale}|${entries}`;
}

/**
 * Returns a cached `Intl.DateTimeFormat` for a locale and options bag.
 * @param locale - A BCP-47 tag
 * @param options - `Intl.DateTimeFormat` options
 * @returns The formatter
 */
function dateFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions | undefined
): Intl.DateTimeFormat {
  const key = cacheKey("d", locale, options);
  const cached = formatterCache.get(key);
  if (cached instanceof Intl.DateTimeFormat) return cached;
  const created = new Intl.DateTimeFormat(locale, options);
  // eslint-disable-next-line functional/immutable-data -- module-private memo cache of a pure function; the Map is never read as state, never exposed, and holds only values fully derivable from the key
  formatterCache.set(key, created);
  return created;
}

/**
 * Returns a cached `Intl.NumberFormat` for a locale and options bag.
 * @param locale - A BCP-47 tag
 * @param options - `Intl.NumberFormat` options
 * @returns The formatter
 */
function numberFormatter(
  locale: string,
  options: Intl.NumberFormatOptions | undefined
): Intl.NumberFormat {
  const key = cacheKey("n", locale, options);
  const cached = formatterCache.get(key);
  if (cached instanceof Intl.NumberFormat) return cached;
  const created = new Intl.NumberFormat(locale, options);
  // eslint-disable-next-line functional/immutable-data -- module-private memo cache of a pure function; see dateFormatter above
  formatterCache.set(key, created);
  return created;
}

/**
 * `Intl.DateTimeFormat` options for the app's most common date shape —
 * "Jan 30, 2026" in English, "30. Jan. 2026" in German.
 *
 * Named rather than repeated at each call site because it was ALREADY repeated
 * at each call site: nine files independently spelled out
 * `{ month: "short", day: "numeric", year: "numeric" }` next to a hardcoded
 * `"en-US"`. Sharing the options is what makes "the same kind of date" a single
 * decision rather than nine that drift.
 *
 * Note these are field REQUESTS, not an output order — `Intl` arranges the
 * parts the way the locale does, which is the whole point.
 */
export const MEDIUM_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

/** Options for a day-and-month date with no year — "Jan 30". */
export const MONTH_DAY_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
};

/** Options for a time of day — "2:45 PM" in English, "14:45" in German. */
export const SHORT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

/** Anything a call site might hold that names a moment in time. */
export type DateLike = Date | string | number;

/**
 * Coerces a {@link DateLike} to a `Date`, or `undefined` when it is not a
 * usable one.
 *
 * Returning `undefined` rather than throwing keeps the "N/A" fallbacks the
 * converted call sites already had: a malformed `createdAt` from the API used
 * to render `Invalid Date`, and that is not an improvement worth shipping.
 * @param value - The value to coerce
 * @returns A valid `Date`, or undefined
 */
function toDate(value: DateLike): Date | undefined {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Formats a date in the active UI locale.
 *
 * Replaces `toLocaleDateString(...)` / `new Intl.DateTimeFormat(...)` at every
 * user-facing call site. The locale is a REQUIRED argument rather than a value
 * this module reads from the i18n singleton, so the function stays pure and
 * testable across locales without monkey-patching globals — the same shape
 * `formatHomeDate` already used. React call sites get the locale from
 * `useLocaleFormat`, which is what makes them re-render on a language change.
 * @param value - The moment to format
 * @param locale - The active UI locale (`en_US` or `en-US`; both work)
 * @param options - `Intl.DateTimeFormat` options
 * @returns The formatted date, or an empty string when the value is not a usable date
 */
export function formatDate(
  value: DateLike,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = toDate(value);
  if (date === undefined) return "";
  return dateFormatter(resolveFormattingLocale(locale), options).format(date);
}

/**
 * Formats a time-of-day in the active UI locale.
 *
 * A thin alias over {@link formatDate} — `Intl.DateTimeFormat` is one formatter
 * for both — kept as a named export so a call site reads as what it renders.
 * @param value - The moment to format
 * @param locale - The active UI locale
 * @param options - `Intl.DateTimeFormat` options
 * @returns The formatted time, or an empty string when the value is not a usable date
 */
export function formatTime(
  value: DateLike,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return formatDate(value, locale, options);
}

/**
 * Formats a number in the active UI locale.
 *
 * This is the half of SE-7966 that decides whether a German user reads
 * `1.234,5` or `1,234.5`. Grouping and decimal separators come from the locale;
 * PRECISION does not — a call site that had a deliberate `maximumFractionDigits`
 * keeps it, because precision is a semantic decision about the statistic and
 * not a formatting default.
 * @param value - The number to format
 * @param locale - The active UI locale
 * @param options - `Intl.NumberFormat` options
 * @returns The formatted number
 */
export function formatNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions
): string {
  return numberFormatter(resolveFormattingLocale(locale), options).format(
    value
  );
}

/**
 * The locale used when there is no active one to read — the same default every
 * other locale surface falls back to.
 *
 * Exported for the non-React call sites (PDF builders, clipboard exports) that
 * run outside a component and have no hook to read.
 */
export const DEFAULT_FORMATTING_LOCALE = toBcp47(DEFAULT_UI_LOCALE);
